import { SecondaryProductSummary } from 'components/secondaryMarket/shared/sections/SecondaryProductSummary';
import { SecondaryTradeInformation } from 'components/secondaryMarket/shared/sections/SecondaryTradeInformation';
import type { Custodian } from 'models/custodian';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC } from 'react';
import type { TradeDateType } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import type { CpOrderSettlement, SalesDesk } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import type { SecondaryPriceType, SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { ClientType } from 'state/secondaryMarket/shared/form.models';
import type { OtcClient, Sales } from 'state/secondaryMarket/shared/referential/referential.model';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';
import type { WorkspaceType } from 'state/workspaces/model';
import type { LoadingStatus } from 'utils/status';

import { CpOrderBookingInformation, CpOrderSalesMarginInformation } from './blocks';

export type CpOrderFormContentProps = Readonly<{
  tabId: string;
  workspaceType: WorkspaceType;
  isinCode?: string;
  denomination?: number;
  fileCode?: string;
  ric?: string;
  maturityDate?: string;
  productClients?: ISalesCounterpart[];
  quotationType?: SecondaryQuotationType;
  priceType?: SecondaryPriceType;
  currency?: string;
  clientType: ClientType;
  isClientVisible: boolean;
  traderGroup?: string;
  isSalesVisible: boolean;
  portfolio?: string;
  isOtcProduct: boolean;
  traderMessage?: string;
  saleMessage?: string;
  quantity: number | undefined;
  cashAmount: number | undefined;
  tradeWay: TradeWay;
  needCustodian: boolean;
  selectedCustodian: Custodian | undefined;
  disabledTradeWays: readonly TradeWay[];
  salesMarginPlusUpfront: number | undefined;
  upfront: number | undefined;
  salesMarginAdjusted: number | undefined;
  tradingPriceAdjusted: number | undefined;
  amountUnit: string;
  cutOffTime: string | undefined;
  cutOffZoneId: string | undefined;
  nextCutOff: Date | undefined;
  settlementDate: string | undefined;
  firstSettlementDate: string | undefined;
  settlementHolidays: ReadonlyArray<string> | undefined;
  valuationDate: string | undefined;
  tradeDateType: TradeDateType;
  availableValuationDates: ReadonlyArray<string> | undefined;
  availableSettlementTypes: ReadonlyArray<CpOrderSettlement> | undefined;
  settlementType: string | undefined;
  cobDate: string | undefined;
  isLoadingSettlement: boolean;
  clientLoadingStatus: LoadingStatus;
  clientEliotCode?: string;
  client?: ISalesCounterpart;
  productOtcClients?: ReadonlyArray<OtcClient>;
  salesId?: string;
  availableSales: Sales[];
  selectedSalesDesk?: string;
  availableDesks: readonly SalesDesk[];
  isFrozen: boolean;
  tradeSizeInputMode: TradeSizeInputMode;
  minQuantity?: number;
  maxQuantity?: number;
  lot?: number;
  remaining?: number;
  isModification: boolean;
  onClientTypeChanged: (value: ClientType) => void;
  onQuantityChanged: (value: number | undefined) => void;
  onQuantityReset: () => void;
  onCashAmountReset: () => void;
  onCashAmountChanged: (value: number | undefined) => void;
  onTradeWayChanged?: (value: TradeWay) => void;
  onCustodianChanged: (value: Custodian | undefined | null) => void;
  onTradingPriceChanged: (value: number | undefined) => void;
  onSalesMarginChanged: (value: number | undefined) => void;
  onSettlementDateChanged: (value: Nullable<string>) => void;
  onValuationDateChanged: (value: Nullable<string>) => void;
  onSettlementTypeChanged: (value: string | undefined) => void;
  onTradeDateTypeChanged: (value: TradeDateType) => void;
  onClientChanged: (client: ISalesCounterpart) => void;
  onClientClear: () => void;
  onSalesChanged: (salesId: string | undefined) => void;
  onSalesDeskChanged: (salesId: string | undefined) => void;
  onTradeSizeInputModeChanged: () => void;
}>;

export const CpOrderFormContent: FC<CpOrderFormContentProps> = ({
  workspaceType,
  clientType,
  isFrozen,
  isClientVisible,
  isOtcProduct,
  isSalesVisible,
  isinCode,
  tabId,
  currency,
  denomination,
  fileCode,
  maturityDate,
  saleMessage,
  traderGroup,
  traderMessage,
  portfolio,
  priceType,
  productClients,
  quotationType,
  ric,
  cashAmount,
  selectedCustodian,
  disabledTradeWays,
  needCustodian,
  quantity,
  salesMarginAdjusted,
  salesMarginPlusUpfront,
  tradingPriceAdjusted,
  amountUnit,
  upfront,
  tradeWay,
  availableSettlementTypes,
  availableValuationDates,
  settlementDate,
  firstSettlementDate,
  settlementHolidays,
  settlementType,
  tradeDateType,
  valuationDate,
  cobDate,
  cutOffTime,
  cutOffZoneId,
  isLoadingSettlement,
  nextCutOff,
  clientLoadingStatus,
  productOtcClients,
  client,
  clientEliotCode,
  availableSales,
  salesId,
  availableDesks,
  selectedSalesDesk,
  tradeSizeInputMode,
  isModification,
  onCashAmountReset,
  onCashAmountChanged,
  onClientTypeChanged,
  onCustodianChanged,
  onQuantityChanged,
  onQuantityReset,
  onTradeWayChanged,
  onSalesMarginChanged,
  onTradingPriceChanged,
  onSettlementDateChanged,
  onSettlementTypeChanged,
  onTradeDateTypeChanged,
  onValuationDateChanged,
  onClientChanged,
  onSalesChanged,
  onSalesDeskChanged,
  onClientClear,
  onTradeSizeInputModeChanged,
  minQuantity,
  maxQuantity,
  lot,
  remaining,
}) => (
  <>
    <SecondaryProductSummary
      clientType={clientType}
      isFrozen={isFrozen}
      isClientVisible={isClientVisible}
      isOtcProduct={isOtcProduct}
      isSalesVisible={isSalesVisible}
      fileCode={fileCode}
      isinCode={isinCode}
      maturityDate={maturityDate}
      portfolio={portfolio}
      priceType={priceType}
      productClients={productClients}
      quotationType={quotationType}
      ric={ric}
      saleMessage={saleMessage}
      traderGroup={traderGroup}
      traderMessage={traderMessage}
      clientLoadingStatus={clientLoadingStatus}
      productOtcClients={productOtcClients}
      client={client}
      clientEliotCode={clientEliotCode}
      availableSales={availableSales}
      salesId={salesId}
      availableDesks={availableDesks}
      selectedSalesDesk={selectedSalesDesk}
      onClientTypeChanged={onClientTypeChanged}
      onClientClear={onClientClear}
      onClientChanged={onClientChanged}
      onSalesChanged={onSalesChanged}
      onSalesDeskChanged={onSalesDeskChanged}
      tabId={tabId}
    />
    <SecondaryTradeInformation
      tabId={tabId}
      cashAmount={cashAmount}
      selectedCustodian={selectedCustodian}
      disabledTradeWays={disabledTradeWays}
      needCustodian={needCustodian}
      isFrozen={isFrozen}
      quantity={quantity}
      tradeWay={tradeWay}
      currency={currency}
      denomination={denomination}
      onCashAmountChanged={onCashAmountChanged}
      onCashAmountReset={onCashAmountReset}
      onCustodianChanged={onCustodianChanged}
      onQuantityChanged={onQuantityChanged}
      onQuantityReset={onQuantityReset}
      onTradeWayChanged={onTradeWayChanged}
      tradeSizeInputMode={tradeSizeInputMode}
      onTradeSizeInputModeChanged={onTradeSizeInputModeChanged}
      minQuantity={minQuantity}
      maxQuantity={maxQuantity}
      lot={lot}
      remaining={remaining}
      isModification={isModification}
    />
    <CpOrderSalesMarginInformation
      tabId={tabId}
      isFrozen={isFrozen}
      salesMarginAdjusted={salesMarginAdjusted}
      salesMarginPlusUpfront={salesMarginPlusUpfront}
      tradingPriceAdjusted={tradingPriceAdjusted}
      amountUnit={amountUnit}
      upfront={upfront}
      onSalesMarginChanged={onSalesMarginChanged}
      onTradingPriceChanged={onTradingPriceChanged}
    />
    <CpOrderBookingInformation
      tabId={tabId}
      availableSettlementTypes={availableSettlementTypes}
      availableValuationDates={availableValuationDates}
      cobDate={cobDate}
      cutOffTime={cutOffTime}
      cutOffZoneId={cutOffZoneId}
      isLoadingSettlement={isLoadingSettlement}
      nextCutOff={nextCutOff}
      settlementDate={settlementDate}
      firstSettlementDate={firstSettlementDate}
      settlementHolidays={settlementHolidays}
      settlementType={settlementType}
      tradeDateType={tradeDateType}
      valuationDate={valuationDate}
      onSettlementDateChanged={onSettlementDateChanged}
      onSettlementTypeChanged={onSettlementTypeChanged}
      onTradeDateTypeChanged={onTradeDateTypeChanged}
      onValuationDateChanged={onValuationDateChanged}
      workspaceType={workspaceType}
      isFrozen={isFrozen}
    />
  </>
);
